import { format, parseISO, addHours, startOfDay, endOfDay } from "date-fns";

/**
 * Unified date utilities for consistent date handling across the application
 */

// Timezone offset for consistent date handling
const TIMEZONE_OFFSET = new Date().getTimezoneOffset() * 60000;

/**
 * Convert any date input to a standardized Date object
 */
export function normalizeDate(dateInput: string | Date | number | null | undefined): Date | null {
  if (!dateInput) return null;
  
  try {
    let date: Date;
    
    if (typeof dateInput === 'string') {
      // Handle ISO strings
      date = new Date(dateInput);
    } else if (typeof dateInput === 'number') {
      if (dateInput > 1000000000000) {
        // Already in milliseconds
        date = new Date(dateInput);
      } else {
        // Convert from seconds to milliseconds
        date = new Date(dateInput * 1000);
      }
    } else if (dateInput instanceof Date) {
      date = new Date(dateInput);
    } else {
      return null;
    }
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn('Invalid date input:', dateInput);
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('Error normalizing date:', error);
    return null;
  }
}

/**
 * Format date for display with consistent formatting
 */
export function formatDate(date: string | Date | number | null, formatStr: string = 'PPP'): string {
  const normalizedDate = normalizeDate(date);
  if (!normalizedDate) return 'Invalid Date';
  
  try {
    return format(normalizedDate, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
}

/**
 * Format time for display
 */
export function formatTime(date: string | Date | number | null, formatStr: string = 'h:mm a'): string {
  const normalizedDate = normalizeDate(date);
  if (!normalizedDate) return 'Invalid Time';
  
  try {
    return format(normalizedDate, formatStr);
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid Time';
  }
}

/**
 * Format date and time together
 */
export function formatDateTime(date: string | Date | number | null): string {
  const normalizedDate = normalizeDate(date);
  if (!normalizedDate) return 'Invalid Date/Time';
  
  try {
    return format(normalizedDate, 'PPP h:mm a');
  } catch (error) {
    console.error('Error formatting date/time:', error);
    return 'Invalid Date/Time';
  }
}

/**
 * Convert frontend date and time to ISO string for backend
 */
export function createEventDateTime(date: Date, startTime: string, endTime: string): {
  startTime: string;
  endTime: string;
} {
  // Parse time strings (e.g., "10:00 AM", "2:30 PM")
  const parseTime = (timeStr: string) => {
    const parts = timeStr.split(/[:\s]/);
    let hour = parseInt(parts[0] || "0");
    const minute = parseInt(parts[1] || "0");
    const period = parts[2]?.toUpperCase();
    
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    
    return { hour, minute };
  };
  
  const startParsed = parseTime(startTime);
  const endParsed = parseTime(endTime);
  
  // Create start date
  const startDate = new Date(date);
  startDate.setHours(startParsed.hour, startParsed.minute, 0, 0);
  
  // Create end date
  const endDate = new Date(date);
  endDate.setHours(endParsed.hour, endParsed.minute, 0, 0);
  
  return {
    startTime: startDate.toISOString(),
    endTime: endDate.toISOString()
  };
}

/**
 * Extract date and time from ISO string for frontend display
 */
export function extractEventDateTime(startTime: string, endTime: string): {
  date: Date;
  startTimeStr: string;
  endTimeStr: string;
} {
  const startDate = normalizeDate(startTime);
  const endDate = normalizeDate(endTime);
  
  if (!startDate || !endDate) {
    throw new Error('Invalid date format');
  }
  
  return {
    date: startDate,
    startTimeStr: formatTime(startDate, 'h:mm a'),
    endTimeStr: formatTime(endDate, 'h:mm a')
  };
}

/**
 * Get relative time description
 */
export function getRelativeTime(date: string | Date | number | null): string {
  const normalizedDate = normalizeDate(date);
  if (!normalizedDate) return 'Invalid Date';
  
  try {
    const now = new Date();
    const diffInMs = normalizedDate.getTime() - now.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 0) {
      return `${Math.abs(diffInDays)} days ago`;
    } else if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Tomorrow';
    } else {
      return `In ${diffInDays} days`;
    }
  } catch (error) {
    console.error('Error getting relative time:', error);
    return 'Invalid Date';
  }
}

/**
 * Check if date is today
 */
export function isToday(date: string | Date | number | null): boolean {
  const normalizedDate = normalizeDate(date);
  if (!normalizedDate) return false;
  
  const today = new Date();
  return format(normalizedDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
}

/**
 * Check if date is in the past
 */
export function isPast(date: string | Date | number | null): boolean {
  const normalizedDate = normalizeDate(date);
  if (!normalizedDate) return false;
  
  return normalizedDate < new Date();
}

/**
 * Check if date is in the future
 */
export function isFuture(date: string | Date | number | null): boolean {
  const normalizedDate = normalizeDate(date);
  if (!normalizedDate) return false;
  
  return normalizedDate > new Date();
}

/**
 * Get days until date
 */
export function getDaysUntil(date: string | Date | number | null): number {
  const normalizedDate = normalizeDate(date);
  if (!normalizedDate) return 0;
  
  const now = new Date();
  const diffInMs = normalizedDate.getTime() - now.getTime();
  return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
} 